import { useRef } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import Check from "lucide-react/dist/esm/icons/check";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import ChevronUp from "lucide-react/dist/esm/icons/chevron-up";

import { cn } from "@/lib/utils";

import { Label } from "@/components/base/Label";

const SelectValue = SelectPrimitive.Value;

/**
 * Exported for triggers that open something other than a Radix `Select`
 * (`ConfigTimezoneControl`'s popover), so every select-like field looks the same.
 */
export const selectTriggerClassName = cn(
  "text-text border-border-strong bg-card flex h-9 w-full cursor-pointer items-center justify-between rounded-lg border px-3",
  "shadow-[inset_0_1px_0_rgba(0,0,0,0.04)]",
  "transition-[box-shadow,border-color] duration-150",
  "focus-visible:border-ring focus-visible:ring-ring/30 focus:outline-none focus-visible:ring-2",
  "data-[state=open]:border-ring data-[state=open]:ring-ring/30 data-[state=open]:ring-2",
  "disabled:bg-surface-subdued disabled:cursor-not-allowed disabled:opacity-70",
  "[&>span]:line-clamp-1"
);

const SelectTrigger: React.FC<SelectPrimitive.SelectTriggerProps> = ({
  className,
  children,
  ...props
}) => (
  <SelectPrimitive.Trigger
    className={cn(selectTriggerClassName, className)}
    {...props}
  >
    <div className="flex flex-row items-center gap-1 text-sm">{children}</div>
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="text-subdued h-4 w-4" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
);

const SelectScrollUpButton: React.FC<
  SelectPrimitive.SelectScrollUpButtonProps
> = ({ className, ...props }) => (
  <SelectPrimitive.ScrollUpButton
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
);

const SelectScrollDownButton: React.FC<
  SelectPrimitive.SelectScrollDownButtonProps
> = ({ className, ...props }) => (
  <SelectPrimitive.ScrollDownButton
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
);

const SelectContent: React.FC<SelectPrimitive.SelectContentProps> = ({
  className,
  children,
  position = "popper",
  onPointerDownOutside,
  onPointerUp,
  onKeyDown,
  onCloseAutoFocus,
  ...props
}) => {
  const closedByPointer = useRef(false);

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className={cn(
          "animate-overlay-in bg-card text-text border-border shadow-popover relative z-[100] max-h-96 min-w-32 overflow-hidden rounded-lg border",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        onPointerDownOutside={(event) => {
          closedByPointer.current = true;
          onPointerDownOutside?.(event);
        }}
        onPointerUp={(event) => {
          closedByPointer.current = true;
          onPointerUp?.(event);
        }}
        onKeyDown={(event) => {
          closedByPointer.current = false;
          onKeyDown?.(event);
        }}
        onCloseAutoFocus={(event) => {
          if (closedByPointer.current) {
            closedByPointer.current = false;
            event.preventDefault();
          }
          onCloseAutoFocus?.(event);
        }}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};

type SelectItemProps = SelectPrimitive.SelectItemProps & {
  /**
   * Help text under the option, shown only in the open list. A sibling of
   * `ItemText` rather than part of it on purpose: Radix clones `ItemText`'s
   * content into the closed trigger, so a description passed as `children`
   * would follow the value out into the collapsed field.
   */
  description?: string;
};

const SelectItem: React.FC<SelectItemProps> = ({
  className,
  children,
  description,
  ...props
}) => (
  <SelectPrimitive.Item
    className={cn(
      "text-text relative flex w-full cursor-pointer rounded-md py-2 pr-3 pl-8 text-[13px] outline-none select-none",
      description ? "flex-col items-start gap-0.5" : "items-center",
      "transition-colors duration-150 ease-out",
      "hover:bg-surface-hovered focus:bg-surface-hovered",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="text-primary h-3.5 w-3.5" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    {description && (
      <span className="text-subdued text-xs leading-4">{description}</span>
    )}
  </SelectPrimitive.Item>
);

type SelectProps = {
  rootProps?: SelectPrimitive.SelectProps;
  triggerProps?: SelectPrimitive.SelectTriggerProps;
  contentProps?: SelectPrimitive.SelectContentProps;
  children: React.ReactNode;
  trigger: React.ReactNode;
  value?: string;
  defaultValue?: string;
  name: string;
  onValueChange?(value: string): void;
  className?: string;
  label?: string;
  helperText?: string;
};

const Select: React.FC<SelectProps> = (props) => {
  return (
    <div className={cn("flex w-full flex-col gap-1", props.className)}>
      {props.label && <Label htmlFor={props.name}>{props.label}</Label>}
      <SelectPrimitive.Root
        {...props.rootProps}
        value={props.value}
        defaultValue={props.defaultValue}
        onValueChange={(value) => {
          if (!props.onValueChange) {
            return;
          }
          props.onValueChange(value);
        }}
      >
        <SelectTrigger
          {...props.triggerProps}
          name={props.name}
          id={props.name}
        >
          {props.trigger}
        </SelectTrigger>

        <SelectContent {...props.contentProps}>{props.children}</SelectContent>
      </SelectPrimitive.Root>
      {props.helperText && <p className="text-subdued">{props.helperText}</p>}
    </div>
  );
};

export { Select, SelectItem, SelectValue };
